import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  CompanySettings,
  PurchaseBillDto,
  PurchaseNoteKind,
  PurchaseNoteSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  bill: PurchaseBillDto;
  /** null حتى تُقرأ الإعدادات — الأزرار لا تظهر قبلها. */
  settings: CompanySettings['purchases'] | null;
  canIssueNotes: boolean;
  canCancel: boolean;
  onChanged: (bill: PurchaseBillDto) => void;
  onSessionLost: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  DEBIT_NOTES_DISABLED: 'الإشعار المدين للمورد مطفأ فى الإعدادات',
  CREDIT_NOTES_DISABLED: 'الإشعار الدائن من المورد مطفأ فى الإعدادات',
  BILL_CANCELLATION_DISABLED: 'إلغاء فواتير الموردين مطفأ فى الإعدادات',
  DEBIT_NOTE_EXCEEDS_OPEN_BALANCE: 'الإشعار المدين أكبر من المتبقى على الفاتورة',
  DEBIT_NOTE_EXCEEDS_BILL: 'مجموع الإشعارات المدينة يتجاوز إجمالى الفاتورة',
  NOTE_BEFORE_BILL: 'تاريخ الإشعار قبل تاريخ الفاتورة',
  BILL_CANCELLED: 'الفاتورة ملغاة',
  BILL_ALREADY_CANCELLED: 'الفاتورة ملغاة بالفعل',
  BILL_HAS_PAYMENTS: 'على الفاتورة سداد — لا تُلغى، استخدم إشعارًا مدينًا',
  BILL_HAS_NOTES: 'على الفاتورة إشعارات — لا تُلغى',
  CANCELLATION_BEFORE_BILL: 'تاريخ الإلغاء قبل تاريخ الفاتورة',
  BILL_HAS_STOCK: 'الفاتورة أدخلت أصنافًا للمخزن — لا تُلغى',
  BILL_LINE_ACCOUNT: 'الحساب يجب أن يكون مصروفًا أو تكلفة أو أصلاً غير رقابى',
  TAX_RULE_NOT_APPROVED: 'القاعدة الضريبية غير معتمدة',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية',
  INVOICE_ZERO_TOTAL: 'الإجمالى يجب أن يكون موجبًا',
};

const KIND_LABELS: Record<PurchaseNoteKind, string> = {
  debit: 'إشعار مدين للمورد',
  credit: 'إشعار دائن من المورد',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

type Mode = { kind: 'note'; noteKind: PurchaseNoteKind } | { kind: 'cancel' } | null;

/**
 * تعديلات فاتورة المورد المنشورة: إشعار مدين (مرتجع أو خصم) أو دائن من المورد، أو إلغاء
 * بقيد عكسى. كل زر يظهر حسب إعداد الشركة وصلاحية المستخدم وحال الفاتورة — والـ API يرفض
 * على أى حال. الإشعار يُبنى على سطر من الفاتورة: حسابه وقاعدته الضريبية، فتُعكس ضريبة
 * المدخلات بنفس أثرها، والمبلغ صافٍ قبل الضريبة.
 */
export default function BillAdjustments({
  companyId,
  currency,
  bill,
  settings,
  canIssueNotes,
  canCancel,
  onChanged,
  onSessionLost,
}: Props) {
  const [notes, setNotes] = useState<PurchaseNoteSummary[]>([]);
  const [mode, setMode] = useState<Mode>(null);
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [lineNumber, setLineNumber] = useState(1);
  const [amount, setAmount] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setProblems(
          issues.length
            ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message)
            : [caught.problem.detail ?? caught.problem.title],
        );
        return;
      }
      setProblems([caught instanceof Error ? caught.message : String(caught)]);
    },
    [onSessionLost],
  );

  const loadNotes = useCallback(() => {
    api.purchaseNotes(companyId, bill.id).then(setNotes).catch(handle);
  }, [companyId, bill.id, handle]);

  useEffect(loadNotes, [loadNotes]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setProblems([]);
  }

  function start(next: Mode): void {
    setMode(next);
    setReason('');
    setDescription('');
    setAmount('');
    setLineNumber(bill.lines[0]?.lineNumber ?? 1);
    setDate(today());
    touched();
  }

  const cancelled = bill.status === 'cancelled';
  const showDebit = canIssueNotes && !cancelled && settings?.debitNotes === true;
  const showCredit = canIssueNotes && !cancelled && settings?.creditNotes === true;
  const showCancel =
    canCancel &&
    !cancelled &&
    settings?.invoiceCancellation === true &&
    Money.of(bill.paid).isZero() &&
    notes.length === 0;

  const baseLine = bill.lines.find((line) => line.lineNumber === lineNumber) ?? bill.lines[0];
  const parsedAmount = parseAmount(amount);
  const ready =
    reason.trim().length >= 3 &&
    (mode?.kind === 'cancel' ||
      (description.trim() !== '' &&
        baseLine !== undefined &&
        parsedAmount !== null &&
        Money.of(parsedAmount).isPositive()));

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!mode || !ready) return;
    setBusy(true);
    setProblems([]);
    try {
      if (mode.kind === 'cancel') {
        const updated = await api.cancelPurchaseBill(companyId, bill.id, idempotencyKey, {
          cancellationDate: date,
          reason: reason.trim(),
        });
        onChanged(updated);
      } else {
        await api.createPurchaseNote(companyId, idempotencyKey, {
          kind: mode.noteKind,
          billId: bill.id,
          noteDate: date,
          reason: reason.trim(),
          lines: [
            {
              description: description.trim(),
              quantity: '1',
              unitPrice: parsedAmount!,
              accountCode: baseLine!.accountCode,
              ...(baseLine!.taxRuleId ? { taxRuleId: baseLine!.taxRuleId } : {}),
            },
          ],
        });
        onChanged(await api.purchaseBill(companyId, bill.id));
        loadNotes();
      }
      setMode(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="no-print">
      <p className="sub">
        المسدد {formatMoney(bill.paid)} • إشعارات مدينة {formatMoney(bill.debitNotesTotal)} • دائنة{' '}
        {formatMoney(bill.creditNotesTotal)} • المتبقى{' '}
        <strong>{formatMoney(bill.openBalance)}</strong> {currency}
      </p>
      {bill.cancellation && (
        <p className="sub">
          <span className="pill bad">ملغاة</span> بتاريخ {bill.cancellation.cancellationDate} —{' '}
          {bill.cancellation.reason} • القيد العكسى {bill.cancellation.journalNumber}
        </p>
      )}

      {notes.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الإشعار</th>
                <th>النوع</th>
                <th>التاريخ</th>
                <th>السبب</th>
                <th>الإجمالى</th>
                <th>القيد</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id}>
                  <td className="mono">{note.number}</td>
                  <td>{KIND_LABELS[note.kind]}</td>
                  <td>{note.noteDate}</td>
                  <td>{note.reason}</td>
                  <td className="num-cell">{formatMoney(note.total)}</td>
                  <td className="mono">{note.journalNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showDebit || showCredit || showCancel) && !mode && (
        <div className="form-actions">
          {showDebit && (
            <button
              type="button"
              className="ghost"
              onClick={() => start({ kind: 'note', noteKind: 'debit' })}
            >
              إشعار مدين / مرتجع
            </button>
          )}
          {showCredit && (
            <button
              type="button"
              className="ghost"
              onClick={() => start({ kind: 'note', noteKind: 'credit' })}
            >
              إشعار دائن من المورد
            </button>
          )}
          {showCancel && (
            <button type="button" className="ghost" onClick={() => start({ kind: 'cancel' })}>
              إلغاء الفاتورة
            </button>
          )}
        </div>
      )}

      {mode && (
        <form onSubmit={(event) => void submit(event)} noValidate>
          <h3>
            {mode.kind === 'cancel'
              ? 'إلغاء فاتورة المورد بقيد عكسى'
              : KIND_LABELS[mode.noteKind] + ' على الفاتورة'}
          </h3>
          <div className="form-row">
            <label>
              التاريخ
              <input
                type="date"
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                  touched();
                }}
              />
            </label>
            <label className="grow">
              السبب
              <input
                value={reason}
                maxLength={500}
                onChange={(event) => {
                  setReason(event.target.value);
                  touched();
                }}
              />
              <Hint>لماذا تُعدَّل فاتورة المورد: مرتجع، خصم لاحق، أو خطأ فى السعر.</Hint>
            </label>
          </div>
          {mode.kind === 'note' && (
            <div className="form-row">
              <label>
                على سطر
                <select
                  value={lineNumber}
                  onChange={(event) => {
                    setLineNumber(Number(event.target.value));
                    touched();
                  }}
                >
                  {bill.lines.map((line) => (
                    <option key={line.lineNumber} value={line.lineNumber}>
                      {line.lineNumber} — {line.description} ({line.accountCode})
                    </option>
                  ))}
                </select>
                <Hint>سطر الفاتورة الأصلية الذى يخصه التعديل، فتُحسب ضريبته بنفس طريقته.</Hint>
              </label>
              <label className="grow">
                البيان
                <input
                  value={description}
                  maxLength={500}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    touched();
                  }}
                />
              </label>
              <label>
                المبلغ قبل الضريبة
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value);
                    touched();
                  }}
                />
                <Hint>اكتب المبلغ بدون ضريبة؛ البرنامج يحسبها ويضيفها.</Hint>
              </label>
            </div>
          )}
          {mode.kind === 'note' && baseLine?.taxRuleId && (
            <p className="sub">تُحسب ضريبة المدخلات بقاعدة السطر نفسها وتُضاف للمبلغ.</p>
          )}
          {mode.kind === 'cancel' && (
            <p className="sub">
              يُرحَّل قيد عكسى كامل بتاريخ الإلغاء، وتبقى الفاتورة وقيدها كما صدرا مع حالة «ملغاة».
              لا رجوع عن الإلغاء.
            </p>
          )}
          {problems.map((problem, i) => (
            <p key={i} className="error">
              {problem}
            </p>
          ))}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy || !ready}>
              {busy ? 'جارى الترحيل…' : mode.kind === 'cancel' ? 'تأكيد الإلغاء' : 'ترحيل الإشعار'}
            </button>
            <button type="button" className="ghost" onClick={() => setMode(null)}>
              تراجع
            </button>
            <Hint block>
              يُسجَّل الإشعار فى حساب المورد ويؤثر على رصيده فورًا. بعد الترحيل لا يُعدَّل ولا يُحذف
              — الخطأ يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
            </Hint>
          </div>
        </form>
      )}
      {!mode &&
        problems.map((problem, i) => (
          <p key={i} className="error">
            {problem}
          </p>
        ))}
    </div>
  );
}
